import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Heart,
  Building2,
  CheckCircle,
  Star,
  Activity,
  Calendar,
  TrendingUp,
  AlertCircle,
  MapPin,
  ChevronRight,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const getHealthScoreColor = (score) => {
  if (score >= 80) return "text-green-400";
  if (score >= 60) return "text-blue-400";
  if (score >= 40) return "text-yellow-400";
  return "text-red-400";
};

const getHealthScoreBg = (score) => {
  if (score >= 80) return "bg-green-500";
  if (score >= 60) return "bg-blue-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getHealthScoreBorder = (score) => {
  if (score >= 80) return "border-green-500/30";
  if (score >= 60) return "border-blue-500/30";
  if (score >= 40) return "border-yellow-500/30";
  return "border-red-500/30";
};

const getHealthStatusText = (score) => {
  if (score >= 80) return "Excellent";
  if (score >= 60) return "Good";
  if (score >= 40) return "Fair";
  return "Needs Attention";
};

const AnnArborHealthPanel = ({ getAccessToken }) => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState("overview"); // overview, top, needsAttention

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = getAccessToken();
        const response = await fetch(`${API_URL}/v2/admin/analytics/ann-arbor/health`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error("Error fetching Ann Arbor health:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessToken]);

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center gap-2 text-gray-400">
          <Activity className="w-5 h-5 animate-spin" />
          Loading Ann Arbor health metrics...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
        <div className="flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>Error loading health metrics</span>
        </div>
        <p className="text-red-300 text-sm mt-2">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { summary, recentActivity, categoryBreakdown, topOrganizations, needsAttention } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Ann Arbor Market Health</h3>
            <p className="text-sm text-gray-400">Primary market performance metrics</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Last 30 days</span>
          <TrendingUp className="w-4 h-4 text-green-400" />
        </div>
      </div>

      {/* Health Score Card */}
      <div className={`p-6 rounded-lg border ${getHealthScoreBorder(summary.averageHealthScore)} bg-gray-800/50`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className={`text-5xl font-bold ${getHealthScoreColor(summary.averageHealthScore)}`}>
                {summary.averageHealthScore}
              </div>
              <div className="text-xs text-gray-500 mt-1">/100 avg health</div>
            </div>
            <div>
              <div className={`text-lg font-medium ${getHealthScoreColor(summary.averageHealthScore)}`}>
                {getHealthStatusText(summary.averageHealthScore)}
              </div>
              <div className="text-sm text-gray-400">
                {needsAttention.length} organizations need attention
              </div>
            </div>
          </div>
          <div className="w-32">
            <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getHealthScoreBg(summary.averageHealthScore)}`}
                style={{ width: `${summary.averageHealthScore}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Building2 className="w-4 h-4" />
            Organizations
          </div>
          <div className="text-2xl font-bold text-white">{summary.totalOrganizations}</div>
          <div className="text-xs text-green-400 mt-1">
            +{recentActivity.organizationsAddedLast30Days} this month
          </div>
        </div>

        <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <CheckCircle className="w-4 h-4" />
            Verified
          </div>
          <div className="text-2xl font-bold text-white">{summary.verifiedOrganizations}</div>
          <div className="text-xs text-gray-400 mt-1">
            {summary.totalOrganizations > 0 
              ? Math.round((summary.verifiedOrganizations / summary.totalOrganizations) * 100)
              : 0}% of total
          </div>
        </div>

        <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Star className="w-4 h-4" />
            Curated
          </div>
          <div className="text-2xl font-bold text-white">{summary.curatedOrganizations}</div>
          <div className="text-xs text-gray-400 mt-1">
            {summary.totalOrganizations > 0 
              ? Math.round((summary.curatedOrganizations / summary.totalOrganizations) * 100)
              : 0}% of total
          </div>
        </div>

        <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Calendar className="w-4 h-4" />
            Programs
          </div>
          <div className="text-2xl font-bold text-white">{summary.totalPrograms}</div>
          <div className="text-xs text-green-400 mt-1">
            +{recentActivity.programsAddedLast30Days} this month
          </div>
        </div>
      </div>

      {/* Sessions Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Activity className="w-4 h-4 text-green-400" />
            With Upcoming Sessions
          </div>
          <div className="text-2xl font-bold text-white">{summary.programsWithSessions}</div>
          <div className="text-xs text-gray-400 mt-1">
            {summary.totalPrograms > 0 
              ? Math.round((summary.programsWithSessions / summary.totalPrograms) * 100)
              : 0}% of programs
          </div>
        </div>

        <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Calendar className="w-4 h-4 text-blue-400" />
            Upcoming Sessions
          </div>
          <div className="text-2xl font-bold text-white">{summary.upcomingSessions}</div>
          <div className="text-xs text-gray-400 mt-1">
            Across all programs
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <button
          type="button"
          onClick={() => setSelectedView("overview")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            selectedView === "overview"
              ? "bg-indigo-600 text-white"
              : "text-gray-400 hover:text-white hover:bg-gray-700"
          }`}
        >
          Categories
        </button>
        <button
          type="button"
          onClick={() => setSelectedView("top")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            selectedView === "top"
              ? "bg-indigo-600 text-white"
              : "text-gray-400 hover:text-white hover:bg-gray-700"
          }`}
        >
          Top Organizations
        </button>
        <button
          type="button"
          onClick={() => setSelectedView("needsAttention")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            selectedView === "needsAttention"
              ? "bg-red-600 text-white"
              : "text-gray-400 hover:text-white hover:bg-gray-700"
          }`}
        >
          Needs Attention
          {needsAttention.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {needsAttention.length}
            </span>
          )}
        </button>
      </div>

      {/* Content based on selected view */}
      {selectedView === "overview" && (
        <div>
          <h4 className="text-white font-medium mb-3">Programs by Category</h4>
          <div className="space-y-2">
            {categoryBreakdown.map((cat) => {
              // Format category ID to display name (e.g., "life-skills" -> "Life Skills")
              const formatCategoryName = (id) => {
                if (!id || id === 'uncategorized') return 'Uncategorized';
                return id
                  .split('-')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
              };
              
              return (
                <div key={cat.category} className="flex items-center gap-3">
                  <div className="w-32 text-sm text-gray-400 truncate">
                    {formatCategoryName(cat.category)}
                  </div>
                  <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-indigo-500 h-full rounded-full"
                      style={{
                        width: `${summary.totalPrograms > 0 ? (cat.program_count / summary.totalPrograms) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <div className="w-12 text-right text-sm text-white">{cat.program_count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedView === "top" && (
        <div>
          <h4 className="text-white font-medium mb-3">Top Performing Organizations</h4>
          <div className="space-y-2">
            {topOrganizations.map((org, index) => (
              <button
                type="button"
                key={org.id}
                onClick={() => history.push(`/org-management?orgId=${org.id}`)}
                className="w-full flex items-center gap-3 p-3 bg-gray-800/30 rounded-lg border border-gray-700 hover:bg-gray-700/50 transition-colors text-left"
              >
                <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-xs text-white font-medium">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">{org.name}</div>
                  <div className="text-xs text-gray-400">
                    {org.programCount} programs
                    {org.isVerified && <span className="ml-2 text-green-400">Verified</span>}
                    {org.isCurated && <span className="ml-2 text-yellow-400">Curated</span>}
                  </div>
                </div>
                <div className={`text-lg font-bold ${getHealthScoreColor(org.healthScore)}`}>
                  {org.healthScore}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedView === "needsAttention" && (
        <div>
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            Organizations Needing Attention
          </h4>
          {needsAttention.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Heart className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <p>All organizations are in good health!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {needsAttention.map((org) => (
                <button
                  type="button"
                  key={org.id}
                  onClick={() => history.push(`/org-management?orgId=${org.id}`)}
                  className="w-full flex items-center gap-3 p-3 bg-red-900/10 rounded-lg border border-red-500/30 hover:bg-red-900/20 transition-colors text-left"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{org.name}</div>
                    <div className="text-xs text-gray-400">
                      {!org.hasDescription && "Missing description"}
                      {!org.hasWebsite && !org.hasDescription && " | "}
                      {!org.hasWebsite && "Missing website"}
                    </div>
                  </div>
                  <div className={`text-lg font-bold ${getHealthScoreColor(org.healthScore)}`}>
                    {org.healthScore}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnnArborHealthPanel;
